/**
 * Session 3D — shared API client for the 31 bespoke /me pages.
 *
 * Per §0.1: every /me endpoint is privacy-enforced — the backend
 * validates `caller_employee_id == requested_data.employee_id`
 * before returning data. Per Session 3a-BACKEND's `useApproverEmployeeId`
 * pattern, the frontend reads the current employee id from a stable
 * storage layer (`useCurrentEmployeeId`) and passes it as a query
 * parameter; the backend doubles-down with an authentication-based
 * resolution path (Clerk user_id → Employee row mapping) when the
 * tenancy + Clerk-employee linkage ships in M3X.AUTH.
 */

import { apiBaseUrl, ApiError, authHeaders } from "./api";

async function _safeGet<T>(path: string, fallback: T): Promise<T> {
  let headers: Record<string, string>;
  try {
    headers = await authHeaders();
  } catch {
    headers = {};
  }
  headers["Content-Type"] = "application/json";
  try {
    const r = await fetch(`${apiBaseUrl}${path}`, { headers });
    if (!r.ok) {
      if (r.status === 404 || r.status === 501) return fallback;
      let detail = r.statusText;
      try {
        const body = await r.json();
        if (body && typeof body.detail === "string") detail = body.detail;
      } catch {
        // Non-JSON body — keep statusText.
      }
      throw new ApiError(r.status, detail);
    }
    return (await r.json()) as T;
  } catch (e) {
    if (e instanceof ApiError) throw e;
    return fallback;
  }
}

function _q(employeeId: string, extra: Record<string, string | number | undefined> = {}): string {
  const params = new URLSearchParams({ employee_id: employeeId });
  for (const [k, v] of Object.entries(extra)) {
    if (v !== undefined && v !== null && v !== "") params.append(k, String(v));
  }
  return params.toString();
}

// ---------------------------------------------------------------------------
// Shared types — one per /me screen.
// ---------------------------------------------------------------------------

export interface HubKpi {
  label: string;
  value: string;
  href: string;
}

export interface HubItem {
  greeting: string;
  employee_first_name_en: string;
  kpis: HubKpi[];
  suggestions: Array<{ headline: string; body: string; href: string | null }>;
  upcoming_events: Array<{ date: string; label: string; kind: string }>;
}

export interface LeaveRequestItem {
  request_id: string;
  leave_type_code: string;
  leave_type_name_en: string;
  start_date: string;
  end_date: string;
  days_requested: number;
  state: "draft" | "submitted" | "pending_approval" | "approved" | "denied" | "cancelled";
  approver_name: string | null;
  days_until_decision: number | null;
}

export interface LeaveBalanceItem {
  leave_type_code: string;
  leave_type_name_en: string;
  current_balance_days: number;
  used_this_year: number;
  pending_requests_days: number;
  carry_forward_from_last_year: number;
  projection_at_year_end: number;
  trace_carried: number;
  trace_accrued: number;
  trace_used: number;
  trace_pending: number;
}

export interface LeaveCalendarDay {
  date: string;
  kind: "approved" | "pending" | "holiday" | "team_overlap";
  label: string;
}

export interface LeaveAdjustmentItem {
  event_id: string;
  date: string;
  leave_type_name_en: string;
  delta_days: number;
  reason: string;
  hr_actor_name: string;
  source: string;
}

export interface PayslipLineItem {
  code: string;
  label: string;
  amount_sar: number;
  trace_text: string;
}

export interface PayslipItem {
  period_label: string;
  pay_date: string;
  earnings: PayslipLineItem[];
  deductions: PayslipLineItem[];
  net_sar: number;
  trace_net: string;
  bank_label: string;
  bank_status: string;
}

export interface PayslipArchiveRow {
  period_label: string;
  pay_date: string;
  gross_sar: number;
  deductions_sar: number;
  net_sar: number;
  status: "paid" | "pending";
}

export interface YtdStatement {
  year: number;
  total_earnings_sar: number;
  earnings_breakdown: Array<{ label: string; amount_sar: number }>;
  total_deductions_sar: number;
  deductions_breakdown: Array<{ label: string; amount_sar: number }>;
  total_net_sar: number;
  gosi_ytd_sar: number;
  loan_repayments_ytd_sar: number;
  tax_ytd_sar: number;
}

export interface DeductionsBreakdownItem {
  code: string;
  label: string;
  amount_sar: number;
  pct_of_gross: number;
  trace_text: string;
}

export interface BankInfo {
  bank_name_en: string;
  account_ending: string;
  status: "verified" | "pending" | "rejected";
  last_verified_at: string | null;
}

export interface GosiItem {
  period_label: string;
  employee_share_sar: number;
  employer_share_sar: number;
  contribution_type: string;
}

export interface GosiStatement {
  ytd_total_sar: number;
  cumulative_total_sar: number;
  projected_pension_monthly_sar: number;
  history: GosiItem[];
}

export interface LoanApplicationItem {
  application_id: string;
  product_code: string;
  product_name_en: string;
  amount_sar: number;
  tenure_months: number;
  state: string;
  submitted_at: string;
  days_in_current_state: number;
}

export interface ActiveLoanItem {
  application_id: string;
  product_name_en: string;
  outstanding_sar: number;
  monthly_installment_sar: number;
  months_remaining: number;
  next_payment_date: string;
  trace_outstanding: string;
}

export interface InstallmentItem {
  installment_no: number;
  due_date: string;
  amount_sar: number;
  paid_date: string | null;
  status: "paid" | "pending" | "skipped";
}

export interface SettlementCalcResult {
  settlement_date: string;
  outstanding_at_date_sar: number;
  early_payment_discount_sar: number;
  final_settlement_sar: number;
  trace_text: string;
}

export interface LoanHistoryItem {
  application_id: string;
  product_name_en: string;
  original_amount_sar: number;
  final_settlement_sar: number;
  settled_date: string;
  status: "settled" | "written_off";
}

export interface AttendanceToday {
  shift_label: string;
  shift_start: string;
  shift_end: string;
  clocked_in_at: string | null;
  clocked_out_at: string | null;
  late_minutes: number;
  status: "not_clocked_in" | "clocked_in" | "clocked_out";
  khidmat_note: string;
}

export interface AttendancePeriodRow {
  date: string;
  clock_in: string | null;
  clock_out: string | null;
  worked_hours: number;
  late_minutes: number;
  overtime_hours: number;
  notes: string | null;
}

export interface ExcuseItem {
  request_id: string;
  date: string;
  excuse_type_name_en: string;
  duration_minutes: number;
  reason: string;
  state: string;
  approver_name: string | null;
}

export interface OvertimeRow {
  date: string;
  hours: number;
  article_107_premium_sar: number;
  trace_premium: string;
  state: string;
  approver_name: string | null;
}

export interface ShiftAssignmentItem {
  shift_label: string;
  start_time: string;
  end_time: string;
  days_of_week_label: string;
  geofence_label: string;
}

export interface TravelTripItem {
  trip_id: string;
  destination: string;
  departure_date: string;
  return_date: string;
  purpose: string;
  state: string;
  per_diem_received_sar: number | null;
}

export interface DocumentItem {
  document_type: string;
  document_number: string;
  issue_date: string | null;
  expiry_date: string | null;
  days_until_expiry: number | null;
  status: "active" | "expiring_soon" | "expired";
  download_url: string | null;
}

export interface EosProjection {
  effective_date: string;
  reason_scenario: string;
  gratuity_sar: number;
  leave_encashment_sar: number;
  loan_deductions_sar: number;
  final_net_sar: number;
  trace_text: string;
}

export interface MyProfile {
  full_name_en: string;
  employee_code: string;
  iqama_national_id: string;
  dob: string | null;
  marital_status: string | null;
  language_preference: "en" | "ar";
  personal_phone: string | null;
  personal_email: string | null;
  emergency_contact_name: string | null;
  emergency_contact_phone: string | null;
  photo_url: string | null;
  bank_iban_last4: string | null;
  position_title: string | null;
  contract_state: string;
}

export interface DirectReportSummary {
  employee_id: string;
  employee_name_en: string;
  position_title: string;
  pending_items_count: number;
  pending_leave_count: number;
  pending_loan_count: number;
  pending_excuse_count: number;
  oldest_pending_days: number | null;
}

export interface TeamView {
  is_manager: boolean;
  direct_reports: DirectReportSummary[];
}

// ---------------------------------------------------------------------------
// API client
// ---------------------------------------------------------------------------

export const session3dApi = {
  getHub: (employeeId: string) =>
    _safeGet<HubItem>(
      `/api/me/hub?${_q(employeeId)}`,
      {
        greeting: "Welcome",
        employee_first_name_en: "",
        kpis: [],
        suggestions: [],
        upcoming_events: [],
      },
    ),

  // Leaves
  listMyLeaveRequests: (employeeId: string, state?: string) =>
    _safeGet<{ items: LeaveRequestItem[] }>(
      `/api/me/leaves/my-requests?${_q(employeeId, { state })}`,
      { items: [] },
    ),
  listMyLeaveBalances: (employeeId: string) =>
    _safeGet<{ items: LeaveBalanceItem[] }>(
      `/api/me/leaves/balances?${_q(employeeId)}`,
      { items: [] },
    ),
  listMyLeaveCalendar: (employeeId: string, fromDate?: string, toDate?: string) =>
    _safeGet<{ items: LeaveCalendarDay[] }>(
      `/api/me/leaves/calendar?${_q(employeeId, { from_date: fromDate, to_date: toDate })}`,
      { items: [] },
    ),
  listMyLeaveAdjustments: (employeeId: string) =>
    _safeGet<{ items: LeaveAdjustmentItem[] }>(
      `/api/me/leaves/adjustments?${_q(employeeId)}`,
      { items: [] },
    ),

  // Payments
  getLatestPayslip: (employeeId: string) =>
    _safeGet<PayslipItem | { period_label: string }>(
      `/api/me/payments/latest?${_q(employeeId)}`,
      {
        period_label: "—",
        pay_date: "—",
        earnings: [] as PayslipLineItem[],
        deductions: [] as PayslipLineItem[],
        net_sar: 0,
        trace_net: "",
        bank_label: "—",
        bank_status: "—",
      } as PayslipItem,
    ),
  listPayslipArchive: (employeeId: string) =>
    _safeGet<{ items: PayslipArchiveRow[] }>(
      `/api/me/payments/archive?${_q(employeeId)}`,
      { items: [] },
    ),
  getYtdStatement: (employeeId: string, year?: number) =>
    _safeGet<YtdStatement>(
      `/api/me/payments/ytd?${_q(employeeId, { year })}`,
      {
        year: year ?? new Date().getFullYear(),
        total_earnings_sar: 0,
        earnings_breakdown: [],
        total_deductions_sar: 0,
        deductions_breakdown: [],
        total_net_sar: 0,
        gosi_ytd_sar: 0,
        loan_repayments_ytd_sar: 0,
        tax_ytd_sar: 0,
      },
    ),
  listDeductionsBreakdown: (employeeId: string) =>
    _safeGet<{ items: DeductionsBreakdownItem[] }>(
      `/api/me/payments/deductions?${_q(employeeId)}`,
      { items: [] },
    ),
  getBankInfo: (employeeId: string) =>
    _safeGet<BankInfo>(
      `/api/me/payments/bank?${_q(employeeId)}`,
      {
        bank_name_en: "—",
        account_ending: "****",
        status: "verified",
        last_verified_at: null,
      },
    ),
  getGosiStatement: (employeeId: string) =>
    _safeGet<GosiStatement>(
      `/api/me/payments/gosi?${_q(employeeId)}`,
      {
        ytd_total_sar: 0,
        cumulative_total_sar: 0,
        projected_pension_monthly_sar: 0,
        history: [],
      },
    ),

  // Loans
  listMyLoanApplications: (employeeId: string) =>
    _safeGet<{ items: LoanApplicationItem[] }>(
      `/api/me/loans/applications?${_q(employeeId)}`,
      { items: [] },
    ),
  listMyActiveLoans: (employeeId: string) =>
    _safeGet<{ items: ActiveLoanItem[] }>(
      `/api/me/loans/active?${_q(employeeId)}`,
      { items: [] },
    ),
  listLoanSchedule: (employeeId: string, applicationId?: string) =>
    _safeGet<{ items: InstallmentItem[] }>(
      `/api/me/loans/schedule?${_q(employeeId, { application_id: applicationId })}`,
      { items: [] },
    ),
  loanSettlementCalc: (employeeId: string, applicationId: string, settlementDate: string) =>
    _safeGet<SettlementCalcResult>(
      `/api/me/loans/settlement-calc?${_q(employeeId, { application_id: applicationId, settlement_date: settlementDate })}`,
      {
        settlement_date: settlementDate,
        outstanding_at_date_sar: 0,
        early_payment_discount_sar: 0,
        final_settlement_sar: 0,
        trace_text: "—",
      },
    ),
  listMyLoanHistory: (employeeId: string) =>
    _safeGet<{ items: LoanHistoryItem[] }>(
      `/api/me/loans/history?${_q(employeeId)}`,
      { items: [] },
    ),

  // Attendance
  getAttendanceToday: (employeeId: string) =>
    _safeGet<AttendanceToday>(
      `/api/me/attendance/today?${_q(employeeId)}`,
      {
        shift_label: "—",
        shift_start: "—",
        shift_end: "—",
        clocked_in_at: null,
        clocked_out_at: null,
        late_minutes: 0,
        status: "not_clocked_in",
        khidmat_note: "—",
      },
    ),
  listAttendancePeriod: (employeeId: string, period?: string) =>
    _safeGet<{ items: AttendancePeriodRow[] }>(
      `/api/me/attendance/period?${_q(employeeId, { period })}`,
      { items: [] },
    ),
  listMyExcuses: (employeeId: string) =>
    _safeGet<{ items: ExcuseItem[] }>(
      `/api/me/attendance/excuses?${_q(employeeId)}`,
      { items: [] },
    ),
  listMyOvertime: (employeeId: string) =>
    _safeGet<{ items: OvertimeRow[] }>(
      `/api/me/attendance/overtime?${_q(employeeId)}`,
      { items: [] },
    ),
  getMyShift: (employeeId: string) =>
    _safeGet<ShiftAssignmentItem>(
      `/api/me/attendance/shift?${_q(employeeId)}`,
      {
        shift_label: "—",
        start_time: "—",
        end_time: "—",
        days_of_week_label: "—",
        geofence_label: "—",
      },
    ),

  // Travel
  listMyTrips: (employeeId: string, scope?: "active" | "reconciliation" | "history") =>
    _safeGet<{ items: TravelTripItem[] }>(
      `/api/me/travel/${scope ?? "history"}?${_q(employeeId)}`,
      { items: [] },
    ),

  // Documents
  listMyDocuments: (employeeId: string) =>
    _safeGet<{ items: DocumentItem[] }>(
      `/api/me/documents?${_q(employeeId)}`,
      { items: [] },
    ),

  // EOS Projection
  getEosProjection: (employeeId: string, effectiveDate?: string, reason?: string) =>
    _safeGet<EosProjection>(
      `/api/me/eos?${_q(employeeId, { effective_date: effectiveDate, reason })}`,
      {
        effective_date: effectiveDate ?? "—",
        reason_scenario: reason ?? "resignation",
        gratuity_sar: 0,
        leave_encashment_sar: 0,
        loan_deductions_sar: 0,
        final_net_sar: 0,
        trace_text: "—",
      },
    ),

  // Profile
  getProfile: (employeeId: string) =>
    _safeGet<MyProfile>(
      `/api/me/profile?${_q(employeeId)}`,
      {
        full_name_en: "—",
        employee_code: "—",
        iqama_national_id: "—",
        dob: null,
        marital_status: null,
        language_preference: "en",
        personal_phone: null,
        personal_email: null,
        emergency_contact_name: null,
        emergency_contact_phone: null,
        photo_url: null,
        bank_iban_last4: null,
        position_title: null,
        contract_state: "—",
      },
    ),

  // Team (manager-only)
  getMyTeam: (employeeId: string) =>
    _safeGet<TeamView>(
      `/api/me/team?${_q(employeeId)}`,
      { is_manager: false, direct_reports: [] },
    ),
};

// ---------------------------------------------------------------------------
// useCurrentEmployeeId — mirrors useApproverEmployeeId from Session 3a-BACKEND.
// ---------------------------------------------------------------------------

/**
 * Storage key for the "I'm acting as this employee" id. Defaults to
 * the URL param `?employee_id=` when present; falls back to localStorage
 * so the choice survives a refresh.
 *
 * Real Clerk-user → Employee resolution ships with M3X.AUTH. Until
 * then the picker on /me/profile lets the user (typically a demo
 * tenant operator) impersonate a specific employee for ESS demos.
 */
const _STORAGE_KEY = "solvait-current-employee-id";

export function readCurrentEmployeeId(): string | null {
  if (typeof window === "undefined") return null;
  const urlParam = new URLSearchParams(window.location.search).get("employee_id");
  if (urlParam) {
    window.localStorage.setItem(_STORAGE_KEY, urlParam);
    return urlParam;
  }
  return window.localStorage.getItem(_STORAGE_KEY);
}

export function writeCurrentEmployeeId(employeeId: string | null): void {
  if (typeof window === "undefined") return;
  if (employeeId) {
    window.localStorage.setItem(_STORAGE_KEY, employeeId);
  } else {
    window.localStorage.removeItem(_STORAGE_KEY);
  }
}
